import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData, unwrapPaginated } from "@/lib/api-response";
import type { PaginatedResult } from "@/types/common";
import type {
  AssignManagerRequest,
  CreateDepartmentRequest,
  Department,
  DepartmentTreeNode,
  ListDepartmentsQuery,
  UpdateDepartmentRequest,
  UpdateDepartmentSettingsRequest,
} from "@/types/department";

export const departmentService = {
  async list(
    query?: ListDepartmentsQuery,
  ): Promise<PaginatedResult<Department>> {
    const response = await http.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
      params: query,
    });
    return unwrapPaginated<Department>(response);
  },

  async tree(): Promise<DepartmentTreeNode[]> {
    const response = await http.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
      params: { tree: true },
    });
    // Backend returns a paginated { items, pagination } envelope.
    return unwrapPaginated<DepartmentTreeNode>(response).items;
  },

  async create(payload: CreateDepartmentRequest): Promise<Department> {
    const response = await http.post(API_ENDPOINTS.DEPARTMENTS.CREATE, payload);
    return unwrapData<Department>(response);
  },

  async get(id: string): Promise<Department> {
    const response = await http.get(API_ENDPOINTS.DEPARTMENTS.DETAIL(id));
    return unwrapData<Department>(response);
  },

  async update(
    id: string,
    payload: UpdateDepartmentRequest,
  ): Promise<Department> {
    const response = await http.put(
      API_ENDPOINTS.DEPARTMENTS.DETAIL(id),
      payload,
    );
    return unwrapData<Department>(response);
  },

  async updateSettings(
    id: string,
    payload: UpdateDepartmentSettingsRequest,
  ): Promise<Department> {
    const response = await http.patch(
      API_ENDPOINTS.DEPARTMENTS.SETTINGS(id),
      payload,
    );
    return unwrapData<Department>(response);
  },

  async assignManager(
    id: string,
    payload: AssignManagerRequest,
  ): Promise<Department> {
    const response = await http.patch(
      API_ENDPOINTS.DEPARTMENTS.ASSIGN_MANAGER(id),
      payload,
    );
    return unwrapData<Department>(response);
  },
};
